import React, { useEffect, useState } from "react";
import { useTranslate } from "@refinedev/core";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Autocomplete,
  CircularProgress,
  Alert,
  Typography,
  Chip,
  Avatar,
  Box,
  Divider,
} from "@mui/material";
import { PersonAdd } from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { SubGroup, CreateSubGroupDto, UpdateSubGroupDto } from "../../hooks/useSubGroups";

export interface AvailableUser {
  id: number;
  fullname: string;
  email: string;
  profilePicture?: string;
}

interface SubGroupFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateSubGroupDto | UpdateSubGroupDto, selectedUserIds?: number[]) => Promise<void>;
  onMove?: (subGroupId: number, newParentId: number | null) => Promise<void>;
  subGroup?: SubGroup | null;
  parentOptions: SubGroup[];
  availableUsers?: AvailableUser[];
  loadingUsers?: boolean;
  loading?: boolean;
  error?: string | null;
}

interface FormData {
  name: string;
  description: string;
  parentId: number | null;
}

export const SubGroupFormDialog: React.FC<SubGroupFormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  onMove,
  subGroup,
  parentOptions,
  availableUsers = [],
  loadingUsers = false,
  loading = false,
  error = null,
}) => {
  const translate = useTranslate();
  const isEditing = !!subGroup;

  // State for selected users (only used during creation)
  const [selectedUsers, setSelectedUsers] = useState<AvailableUser[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      name: "",
      description: "",
      parentId: null,
    },
  });

  // Reset form when dialog opens or subGroup changes
  useEffect(() => {
    if (open) {
      reset({
        name: subGroup?.name || "",
        description: subGroup?.description || "",
        parentId: subGroup?.parentId || null,
      });
      // Reset selected users when dialog opens (only for creation)
      if (!subGroup) {
        setSelectedUsers([]);
      }
    }
  }, [open, subGroup, reset]);

  const handleFormSubmit = async (data: FormData) => {
    const payload: CreateSubGroupDto | UpdateSubGroupDto = isEditing
      ? {
          name: data.name,
          description: data.description || undefined,
        }
      : {
          name: data.name,
          description: data.description || undefined,
          parentId: data.parentId || undefined,
        };

    // Pass selected user IDs only during creation
    const userIds = !isEditing && selectedUsers.length > 0
      ? selectedUsers.map(u => u.id)
      : undefined;

    await onSubmit(payload, userIds);

    // If editing and parent has changed, call the move endpoint
    if (isEditing && subGroup && onMove) {
      const currentParentId = subGroup.parentId;
      const newParentId = data.parentId;
      if (currentParentId !== newParentId) {
        await onMove(subGroup.id, newParentId);
      }
    }
  };

  // Filter out current subgroup and its descendants from parent options
  const availableParents = parentOptions.filter((p) => {
    if (!subGroup) return true;
    // Cannot set itself as parent
    if (p.id === subGroup.id) return false;
    // For now, simple check - in production would need to check all descendants
    return true;
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogTitle>
          {isEditing
            ? translate("company.subGroups.editTitle", "Modifier le sous-groupe")
            : translate("company.subGroups.createTitle", "Cr\u00e9er un sous-groupe")}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Controller
              name="name"
              control={control}
              rules={{
                required: translate("validation.required", "Ce champ est requis"),
                minLength: {
                  value: 2,
                  message: translate("validation.minLength", "Minimum 2 caract\u00e8res"),
                },
                maxLength: {
                  value: 100,
                  message: translate("validation.maxLength", "Maximum 100 caract\u00e8res"),
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={translate("company.subGroups.fields.name", "Nom")}
                  fullWidth
                  required
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  placeholder={translate("company.subGroups.namePlaceholder", "\u00c9quipe Marketing")}
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              rules={{
                maxLength: {
                  value: 500,
                  message: translate("validation.maxLength500", "Maximum 500 caract\u00e8res"),
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={translate("company.subGroups.fields.description", "Description")}
                  fullWidth
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  placeholder={translate(
                    "company.subGroups.descriptionPlaceholder",
                    "\u00c9quipe responsable du marketing digital"
                  )}
                />
              )}
            />

            <Controller
              name="parentId"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  options={availableParents}
                  getOptionLabel={(option) => option.name}
                  value={availableParents.find((p) => p.id === value) || null}
                  onChange={(_, newValue) => onChange(newValue?.id || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={translate("company.subGroups.fields.parent", "Groupe parent")}
                      placeholder={translate(
                        "company.subGroups.parentPlaceholder",
                        "Aucun (groupe racine)"
                      )}
                      helperText={isEditing
                        ? translate("company.subGroups.parentHelperEdit", "Modifier le parent déplacera ce sous-groupe dans la hiérarchie")
                        : undefined
                      }
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <span style={{ paddingLeft: (option.depth || 0) * 16 }}>
                        {option.name}
                      </span>
                    </li>
                  )}
                />
              )}
            />

            {/* User selection - only during creation */}
            {!isEditing && availableUsers.length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonAdd fontSize="small" />
                    {translate("company.subGroups.assignUsers", "Assigner des utilisateurs (optionnel)")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    {translate("company.subGroups.assignUsersHelp", "Sélectionnez les utilisateurs à ajouter à ce sous-groupe lors de sa création.")}
                  </Typography>
                  <Autocomplete
                    multiple
                    options={availableUsers}
                    getOptionLabel={(option) => option.fullname || option.email}
                    value={selectedUsers}
                    onChange={(_, newValue) => setSelectedUsers(newValue)}
                    loading={loadingUsers}
                    disableCloseOnSelect
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={translate("company.subGroups.selectUsers", "Utilisateurs")}
                        placeholder={selectedUsers.length === 0
                          ? translate("company.subGroups.selectUsersPlaceholder", "Rechercher des utilisateurs...")
                          : ""
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            src={option.profilePicture}
                            sx={{ width: 32, height: 32 }}
                          >
                            {option.fullname?.[0]?.toUpperCase() || option.email?.[0]?.toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2">{option.fullname}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.email}
                            </Typography>
                          </Box>
                        </Box>
                      </li>
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          avatar={
                            <Avatar src={option.profilePicture}>
                              {option.fullname?.[0]?.toUpperCase()}
                            </Avatar>
                          }
                          label={option.fullname}
                          size="small"
                        />
                      ))
                    }
                  />
                  {selectedUsers.length > 0 && (
                    <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                      {translate("company.subGroups.usersSelected", { count: selectedUsers.length })}
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={isSubmitting || loading}>
            {translate("buttons.cancel", "Annuler")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || loading}
            startIcon={(isSubmitting || loading) && <CircularProgress size={16} />}
          >
            {isEditing
              ? translate("buttons.save", "Enregistrer")
              : translate("buttons.create", "Cr\u00e9er")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SubGroupFormDialog;
